import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import useEscapeKey from "@hooks/useEscapeKey";
import { useClientConfig } from "@contexts/ClientConfigContext";
import { OutlineButton, PrimaryButton } from "@components/Buttons";
import CustomCheckbox from "@components/Forms/CustomCheckbox";
import CustomRadio from "@components/Forms/CustomRadio";
import CustomSelect from "./CustomSelect";
import {
  SBOM_EXPORT_EXCEL,
  SBOM_EXPORT_HTML_READABLE,
  SBOM_FORMAT_HTML,
  SBOM_NAME_CYCLONEDX_1_6,
  SBOM_NAME_EXCEL,
  SBOM_NAME_HTML_READABLE,
  SBOM_NAME_SPDX_2_3,
  SBOM_NAME_SPDX_3_0,
  SBOM_VERIFICATION_TYPE_CYCLONEDX,
  SBOM_VERIFICATION_TYPE_EXCEL,
  SBOM_VERIFICATION_TYPE_SPDX_2_3,
  SBOM_VERIFICATION_TYPE_SPDX_3,
  isSbomHtmlReadableExport,
} from "@constants/sbom";

export const CSAF_STANDARD_VALUE = "csaf";
export const EXCEL_STANDARD_VALUE = SBOM_EXPORT_EXCEL;

const FORMAT_JSON = "JSON";
const FORMAT_XML = "XML";
const FORMAT_RDF_XML = "RDF/XML";
const FORMAT_XLSX = "XLSX";
const FORMAT_CSAF = "CSAF";
const FORMAT_HTML = SBOM_FORMAT_HTML;

// Canonical sbomType keys — same as verification import / download APIs
const EXPORT_TYPE_OPTIONS = [
  { value: SBOM_VERIFICATION_TYPE_SPDX_2_3, label: SBOM_NAME_SPDX_2_3, sbomName: SBOM_NAME_SPDX_2_3 },
  { value: SBOM_VERIFICATION_TYPE_SPDX_3, label: SBOM_NAME_SPDX_3_0, sbomName: SBOM_NAME_SPDX_3_0 },
  { value: SBOM_VERIFICATION_TYPE_CYCLONEDX, label: SBOM_NAME_CYCLONEDX_1_6, sbomName: SBOM_NAME_CYCLONEDX_1_6 },
  { value: CSAF_STANDARD_VALUE, label: "CSAF 2.0" },
  { value: SBOM_VERIFICATION_TYPE_EXCEL, label: SBOM_NAME_EXCEL, sbomName: SBOM_NAME_EXCEL },
  {
    value: SBOM_EXPORT_HTML_READABLE,
    label: SBOM_NAME_HTML_READABLE,
    sbomName: SBOM_NAME_HTML_READABLE,
  },
];

const buildExportTypeOptions = (isVexOn) =>
  EXPORT_TYPE_OPTIONS.filter(
    (option) => option.value !== CSAF_STANDARD_VALUE || isVexOn,
  );

// Formats allowed per export type.
const FORMAT_OPTIONS_BY_TYPE = {
  [SBOM_VERIFICATION_TYPE_SPDX_2_3]: [
    { value: FORMAT_JSON, label: FORMAT_JSON },
    { value: FORMAT_XML, label: FORMAT_XML },
    { value: FORMAT_RDF_XML, label: FORMAT_RDF_XML },
  ],
  [SBOM_VERIFICATION_TYPE_SPDX_3]: [{ value: FORMAT_JSON, label: FORMAT_JSON }],
  [SBOM_VERIFICATION_TYPE_CYCLONEDX]: [
    { value: FORMAT_JSON, label: FORMAT_JSON },
    { value: FORMAT_XML, label: FORMAT_XML },
  ],
  [CSAF_STANDARD_VALUE]: [{ value: FORMAT_JSON, label: FORMAT_JSON }],
  [SBOM_VERIFICATION_TYPE_EXCEL]: [{ value: FORMAT_XLSX, label: "Excel (.xlsx)" }],
  [SBOM_EXPORT_HTML_READABLE]: [{ value: FORMAT_HTML, label: FORMAT_HTML }],
};

// Export types where VEX can be toggled by the user
const VEX_OPTIONAL_TYPES = new Set([
  SBOM_VERIFICATION_TYPE_SPDX_3,
  SBOM_VERIFICATION_TYPE_CYCLONEDX,
  SBOM_EXPORT_HTML_READABLE,
]);
// CSAF always includes VEX — show a checked, disabled checkbox for clarity
const VEX_ALWAYS_INCLUDED_TYPES = new Set([CSAF_STANDARD_VALUE]);

const isVexVisibleForType = (typeValue) =>
  VEX_OPTIONAL_TYPES.has(typeValue) || VEX_ALWAYS_INCLUDED_TYPES.has(typeValue);

const ExportSbomReportModal = ({
  isOpen,
  onClose,
  exporting = false,
  onExport,
  "data-testid": dataTestId = "export-sbom-report-modal",
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const clientConfig = useClientConfig();
  const isVexOn = Boolean(clientConfig?.isClientConfigEnabled?.("useVex"));

  const [type, setType] = useState(null);
  const [format, setFormat] = useState(FORMAT_JSON);
  const [includeVex, setIncludeVex] = useState(true);

  const exportTypeOptions = buildExportTypeOptions(isVexOn).map((option) =>
    option.value === SBOM_EXPORT_HTML_READABLE
      ? { ...option, label: t("project.verification-report.html-readable", SBOM_NAME_HTML_READABLE) }
      : option,
  );

  // Reset selections each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setType(null);
      setFormat(FORMAT_JSON);
      setIncludeVex(true);
    }
  }, [isOpen]);

  // Each export type has its own format set — reset to the first allowed format
  useEffect(() => {
    const options = type ? FORMAT_OPTIONS_BY_TYPE[type.value] || [] : [];
    setFormat(options[0]?.value || null);
  }, [type]);

  // CSAF always includes VEX — keep checkbox checked when that type is selected
  useEffect(() => {
    if (type?.value === CSAF_STANDARD_VALUE) {
      setIncludeVex(true);
    }
  }, [type]);

  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  const formatOptions = type ? FORMAT_OPTIONS_BY_TYPE[type.value] || [] : [];
  const isFormatStatic = formatOptions.length <= 1;
  const showVex = isVexOn && !!type && isVexVisibleForType(type.value);
  const isVexLocked = !!type && VEX_ALWAYS_INCLUDED_TYPES.has(type.value);
  const canExport = !!type && !!format && !exporting;

  const handleExport = () => {
    onExport({
      type,
      format: type?.value === CSAF_STANDARD_VALUE ? FORMAT_CSAF : format,
      includeVex: showVex ? (isVexLocked ? true : includeVex) : false,
    });
  };

  return (
    <div
      className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start md:justify-center backdrop-blur-[1px] overflow-x-auto p-4`}
      data-testid={`${dataTestId}-overlay`}
    >
      <div className={`${colors.bgModal} max-h-[90vh] w-[800px] min-w-[800px] flex-shrink-0 rounded-lg shadow-xl flex flex-col`} data-testid={dataTestId}>
        {/* Header */}
        <div className={`px-6 py-4 flex-shrink-0`} data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-xl font-bold`} data-testid={`${dataTestId}-title`}>
              {t("project.verification-report.export-sbom-report")}
            </h2>
          </div>
        </div>

        {/* Body */}
        <div className="space-y-4 px-6 pb-4 flex-1 min-h-0 overflow-y-auto" data-testid={`${dataTestId}-body`}>
          <div data-testid={`${dataTestId}-field-type`}>
            <label className={`mb-1 block text-sm font-medium ${colors.textSubTitle}`}>
              {t("project.verification-report.export-type")}
            </label>
            <CustomSelect
              options={exportTypeOptions}
              value={type}
              onChange={setType}
              placeholder={t("common.please-select")}
              clearable={false}
              usePortal
              data-testid={`${dataTestId}-type`}
            />
          </div>

          {type && formatOptions.length > 0 && (
            <div data-testid={`${dataTestId}-field-format`}>
              <label className={`mb-1 block text-sm font-medium ${colors.textSubTitle}`}>
                {t("project.verification-report.format")}
              </label>
              {isFormatStatic ? (
                <p className={`text-sm font-medium ${colors.textPrimary}`} data-testid={`${dataTestId}-format-value`}>
                  {formatOptions[0].label}
                </p>
              ) : (
                <div className="flex items-center gap-6">
                  {formatOptions.map((option) => (
                    <CustomRadio
                      key={option.value}
                      name={`${dataTestId}-format`}
                      value={option.value}
                      checked={format === option.value}
                      onChange={() => setFormat(option.value)}
                      label={option.label}
                      data-testid={`${dataTestId}-format-${option.value}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {showVex && (
            <div data-testid={`${dataTestId}-field-vex`}>
              <label className={`mb-1 block text-sm font-medium ${colors.textSubTitle}`}>
                {t("project.verification-report.vex")}
              </label>
              <CustomCheckbox
                checked={includeVex}
                onChange={(e) => setIncludeVex(e.target.checked)}
                disabled={isVexLocked}
                label={t("project.verification-report.include-vex")}
                data-testid={`${dataTestId}-vex-checkbox`}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 ${colors.borderPrimary} flex-shrink-0`} data-testid={`${dataTestId}-footer`}>
          <div className="flex items-center justify-end">
            <div className="flex items-center space-x-3">
              <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={onClose}>
                {t("common.cancel-button")}
              </OutlineButton>
              <PrimaryButton
                data-testid={`${dataTestId}-export-button`}
                onClick={handleExport}
                disabled={!canExport}
              >
                {exporting
                  ? t("project.verification-report.exporting", "Exporting...")
                  : isSbomHtmlReadableExport(type)
                    ? t("project.verification-report.view-html", "View HTML")
                    : t("project.verification-report.export", "Export")}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportSbomReportModal;
